'use client';

import Image from 'next/image';
import { useCallback, useState } from 'react';

/** ~2-col mobile, 3-col tablet, 4-col desktop listing grids — keeps requested width near real display size. */
export const LISTING_GRID_SIZES = '(max-width: 640px) 50vw, (max-width: 1024px) 34vw, 25vw';

function isHttpUrl(src: string): boolean {
  return /^https?:\/\//i.test(src.trim());
}

type ListingThumbnailProps = {
  src: string;
  alt: string;
  className?: string;
  sizes?: string;
  priority?: boolean;
  /** If set, swap to this `img` src when the remote image fails (e.g. SVG data URI). */
  fallbackSrc?: string;
};

function parseImageClasses(className: string) {
  const objectFit = className.includes('object-contain')
    ? 'object-contain'
    : className.includes('object-fill')
      ? 'object-fill'
      : 'object-cover';
  const hoverScale = /\bgroup-hover:scale-[^\s]+/.test(className);
  const rest = className
    .replace(/\bobject-(contain|cover|fill|none|scale-down)\b/g, '')
    .replace(/\bgroup-hover:scale-[^\s]+\b/g, '')
    .replace(/\btransition-transform\b/g, '')
    .replace(/\bduration-\d+\b/g, '')
    .trim();
  return { objectFit, hoverScale, rest };
}

/**
 * Card hero image: uses Next.js image optimization for http(s) sources so the browser
 * loads a resized file instead of the full-resolution original.
 */
export function ListingThumbnail({
  src,
  alt,
  className = '',
  sizes = LISTING_GRID_SIZES,
  priority = false,
  fallbackSrc,
}: ListingThumbnailProps) {
  const [useFallback, setUseFallback] = useState(false);
  const { objectFit, hoverScale, rest } = parseImageClasses(className);
  const imageClass = [objectFit, 'object-center', rest].filter(Boolean).join(' ');

  const onError = useCallback(() => {
    if (fallbackSrc) setUseFallback(true);
  }, [fallbackSrc]);

  const wrap = (node: React.ReactElement) => (
    <span className="absolute inset-0 block overflow-hidden">
      <span
        className={[
          'absolute inset-0 size-full',
          hoverScale ? 'transition-transform duration-500 group-hover:scale-105' : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {node}
      </span>
    </span>
  );

  if (useFallback && fallbackSrc) {
    return wrap(
      <img
        src={fallbackSrc}
        alt={alt}
        className={`h-full w-full min-h-full min-w-full ${imageClass}`}
        loading={priority ? 'eager' : 'lazy'}
      />,
    );
  }

  if (!isHttpUrl(src)) {
    return wrap(
      <img
        src={src}
        alt={alt}
        className={`h-full w-full min-h-full min-w-full ${imageClass}`}
        loading={priority ? 'eager' : 'lazy'}
        onError={fallbackSrc ? onError : undefined}
      />,
    );
  }

  return wrap(
    <Image
      src={src}
      alt={alt}
      fill
      sizes={sizes}
      className={imageClass}
      priority={priority}
      quality={72}
      onError={fallbackSrc ? onError : undefined}
    />,
  );
}
